import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCircle2,
  ChevronDown,
  Edit3,
  ExternalLink,
  LogIn,
  ListTree,
  Mail,
  MinusCircle,
  Settings as SettingsIcon,
  Smartphone,
  User as UserIcon,
  UserPlus,
} from 'lucide-react';
import type {
  ProptreeListQuery,
  ProptreeListResponse,
  ProptreeWithAgent,
} from '@proptr/shared';
import { useAuth } from '@proptr/api-client/react';
import { EmptyState } from '../components/EmptyState.js';
import {
  HeaderLabel,
  SortHeader,
  TableCard,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.js';
import { BioChannelsCell } from '../components/BioChannelsCell.js';
import { SettingsCard } from '../components/SettingsCard.js';
import {
  StatusBadge,
  isActiveStatus,
  statusLabel as displayStatus,
} from '../components/StatusBadge.js';
import { isTestMode, withDemoFlag } from '../lib/testMode.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

// Where the agent portal lives. Manager-side "Login as Proptree" opens
// it in a new tab with the impersonation bearer token in the URL.
const APP_URL =
  (import.meta.env.VITE_APP_URL as string | undefined) ??
  'http://localhost:5173';
// Where public proptree pages render. Manager-side "Open Proptree" opens
// `<PUBLIC_URL>/<alias-or-shortId>` in a new tab.
const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  'http://localhost:4040';

type StatusFilter = NonNullable<ProptreeListQuery['status']>;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'enabled', label: 'Active proptrees' },
  { value: 'disabled', label: 'Disabled proptrees' },
  { value: 'pending', label: 'Pending proptrees' },
  { value: 'notcreated', label: 'Agents without proptree' },
  { value: 'all', label: 'All proptrees' },
];

// Short labels for the page-title subtitle. We omit `all` (it's the total
// shown first) and only render rows whose count is > 0 — see render below.
const STATUS_BREAKDOWN: {
  key: 'enabled' | 'disabled' | 'pending' | 'notcreated';
  label: string;
}[] = [
  { key: 'enabled', label: 'active' },
  { key: 'disabled', label: 'disabled' },
  { key: 'pending', label: 'pending' },
  { key: 'notcreated', label: 'without proptree' },
];

export function ProptreePage() {
  const navigate = useNavigate();
  // Admin-only columns (admin/welcome/reminder/report status) only render
  // when the session was minted by an admin via /api/auth/admin/login-as
  // — i.e. when `impersonator` is non-null. Real managers never see them.
  const { impersonator } = useAuth();
  const isImpersonating = impersonator != null;
  const [sortBy, setSortBy] = React.useState<
    NonNullable<ProptreeListQuery['sort']>
  >('createdAt');
  const [order, setOrder] = React.useState<
    NonNullable<ProptreeListQuery['order']>
  >('desc');

  // Click a column to sort by it; click again to flip direction. New
  // column starts descending (highest first), which is what you want on
  // every metric column here.
  const handleSort = (key: NonNullable<ProptreeListQuery['sort']>) => {
    if (sortBy === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setOrder('desc');
    }
    setPage(1);
  };
  const [status, setStatus] = React.useState<StatusFilter>('enabled');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [data, setData] = React.useState<ProptreeListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // True while a filter/sort/page change refetches with rows
  // already on screen — drives TableCard's dim + "Updating" pill.
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    client.proptrees
      .listProptrees({ status, sort: sortBy, order, page, limit: pageSize })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [status, sortBy, order, page, pageSize]);

  const statusLabel =
    STATUS_OPTIONS.find((s) => s.value === status)?.label ?? 'Proptrees';

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;
  // Pinned above the agent rows, unaffected by sort/pagination.
  const agency = data?.agency ?? null;

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Proptree
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              {meta ? (
                <>
                  <span className="text-foreground">{meta.counts.all}</span>{' '}
                  proptrees in organization
                  {STATUS_BREAKDOWN.map(({ key, label }) => {
                    const n = meta.counts[key];
                    if (n === 0) return null;
                    return (
                      <React.Fragment key={key}>
                        <span className="mx-2 text-muted-foreground/60">·</span>
                        <span className="text-foreground">{n}</span> {label}
                      </React.Fragment>
                    );
                  })}
                </>
              ) : (
                'Loading…'
              )}
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap gap-3">
            <SettingsCard
              title="Agency proptree"
              subtitle="Configure your agency proptree settings"
              onAction={() => navigate('/proptree/agency')}
            />
            <SettingsCard
              title="Agent proptree"
              subtitle="Configure your agents proptree settings"
              onAction={() => navigate('/proptree/agents')}
            />
          </div>
        </div>

        <TableCard
          minWidth={1180}
          busy={refreshing && data !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
            <table
              className={cn(
                'w-full border-collapse',
                // Stretch only when the body has no rows, so the empty /
                // loading state can centre. With rows present this would
                // divide the leftover height across them.
                (!items || items.length === 0) && 'flex-1',
              )}
            >
              <thead>
                <tr className="border-b border-[hsl(var(--hairline))]">
                  <th className="whitespace-nowrap px-4 py-3 text-left align-middle font-normal first:pl-5 last:pr-5">
                    <div className="flex items-center gap-3">
                      <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="mono-label group inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-background px-2.5 py-1 text-muted-foreground shadow-sm transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                        >
                          <span>{statusLabel}</span>
                          <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="min-w-[16rem]"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <DropdownMenuItem
                            key={opt.value}
                            onSelect={() => {
                              setStatus(opt.value);
                              setPage(1);
                            }}
                            className={cn(
                              'flex items-center justify-between gap-4',
                              opt.value === status && 'text-foreground',
                            )}
                          >
                            <span>{opt.label}</span>
                            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                              {meta?.counts?.[opt.value] ?? 0}
                            </span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                  </th>
                  {(
                    [
                      { label: 'Agent', key: 'agent' },
                      { label: 'Link in bio' },
                      { label: 'Status' },
                      ...(isImpersonating
                        ? [
                            { label: 'Admin status' },
                            { label: 'Welcome status' },
                            { label: 'Reminder status' },
                            { label: 'Report status' },
                          ]
                        : []),
                      { label: 'Listings', key: 'activeListings' },
                      { label: 'Views', key: 'pageViews' },
                      { label: 'Clicks', key: 'listingClicks' },
                      { label: 'Inquiries', key: 'listingInquiries' },
                      { label: 'Buttons', key: 'activeButtons' },
                      { label: 'Btn clicks', key: 'buttonClicks' },
                      { label: 'Created', key: 'createdAt' },
                      { label: 'Recovery' },
                    ] as Array<{
                      label: string;
                      key?: NonNullable<ProptreeListQuery['sort']>;
                    }>
                  ).map(({ label, key }) => (
                    <th
                      key={label}
                      className="whitespace-nowrap px-4 py-3 text-left align-middle font-normal first:pl-5 last:pr-5"
                    >
                      {key ? (
                        <SortHeader
                          label={label}
                          sortKey={key}
                          sortBy={sortBy}
                          order={order}
                          onSort={handleSort}
                        />
                      ) : (
                        <HeaderLabel>{label}</HeaderLabel>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr>
                    <td colSpan={isImpersonating ? 16 : 12} className="h-full">
                      <TableMessage tone="destructive">{error}</TableMessage>
                    </td>
                  </tr>
                ) : items === null ? (
                  <tr>
                    <td colSpan={isImpersonating ? 16 : 12} className="h-full">
                      <TableLoading />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={isImpersonating ? 16 : 12} className="h-full">
                      <EmptyState
                        icon={ListTree}
                        title="No proptrees yet"
                        description="Every agent gets their own proptree — a personal link-in-bio page. Add agents and their pages show up here."
                      />
                    </td>
                  </tr>
                ) : (
                  <>
                    {agency && (
                      <AgencyPinnedRow
                        agency={agency}
                        colSpan={isImpersonating ? 16 : 12}
                        showAdminColumns={isImpersonating}
                      />
                    )}
                    <SectionLabelRow
                      label="Agent proptrees"
                      colSpan={isImpersonating ? 16 : 12}
                    />
                    {items.map((proptree, i) => (
                      <ProptreeRow
                        key={proptree._id}
                        proptree={proptree}
                        statusFilter={status}
                        showAdminColumns={isImpersonating}
                        first={i === 0}
                      />
                    ))}
                  </>
                )}
              </tbody>
            </table>
        </TableCard>
      </div>
    </AppShell>
  );
}

// Small heading row that introduces a group of rows ("Agency proptree",
// "Agent proptrees"). A full-width cell so it isn't constrained by the
// column layout underneath it.
function SectionLabelRow({
  label,
  colSpan,
}: {
  label: string;
  colSpan: number;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 pb-1.5 pt-4">
        <div className="mono-label text-muted-foreground">{label}</div>
      </td>
    </tr>
  );
}

// The brokerage's own proptree, pinned above the agent rows and outside
// sort/pagination. Rendered as a real table row so every value sits under
// its proper column header, with the cells outlined so it reads as a
// distinct block rather than just another agent.
function AgencyPinnedRow({
  agency,
  colSpan,
  showAdminColumns,
}: {
  agency: ProptreeWithAgent;
  colSpan: number;
  showAdminColumns: boolean;
}) {
  const navigate = useNavigate();
  const handle = agency.alias ? `@${agency.alias}` : '—';
  const company = agency.profileFirstName || 'Agency proptree';
  const publicSlug = agency.alias || agency.shortId;
  const openPublic = () => {
    if (!publicSlug) return;
    window.open(
      withDemoFlag(`${PUBLIC_URL}/${encodeURIComponent(publicSlug)}`),
      '_blank',
      'noopener,noreferrer',
    );
  };
  // Box outline lives on the cells (a <tr> can't carry a border-radius).
  const box =
    'border-y border-[hsl(var(--hairline))] bg-muted/30 first:border-l last:border-r';

  return (
    <>
      <SectionLabelRow label="Agency proptree" colSpan={colSpan} />
      <tr className="[&>td]:px-4 [&>td]:py-4 [&>td]:align-middle [&>td:first-child]:pl-5 [&>td:last-child]:pr-5">
        <td className={box}>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:bg-muted focus-visible:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
                  aria-label="Agency proptree actions"
                >
                  <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[14rem]">
                <DropdownMenuItem onSelect={openPublic} disabled={!publicSlug}>
                  <span className="flex-1">Open Proptree</span>
                  <ExternalLink />
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/proptree/agency')}>
                  <span className="flex-1">Edit</span>
                  <Edit3 />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ProfileAvatar name={company} image={agency.profileImage} />
            <div>
              <div className="whitespace-nowrap font-sans text-[14px] font-medium text-foreground">
                {handle}
              </div>
              <div className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                {agency.shortId ?? '—'}
              </div>
            </div>
          </div>
        </td>
        <td className={box}>
          <LabeledCell value={company} />
        </td>
        <td className={box}>
          <BioChannelsCell channels={null} />
        </td>
        <td className={box}>
          <StatusBadge
            label={displayStatus(agency.status)}
            tone={isActiveStatus(agency.status) ? 'success' : 'muted'}
          />
        </td>
        {showAdminColumns && (
          <>
            <td className={box}><LabeledCell value="—" /></td>
            <td className={box}><LabeledCell value="— / —" mono /></td>
            <td className={box}><LabeledCell value="— / —" mono /></td>
            <td className={box}><LabeledCell value="— / —" mono /></td>
          </>
        )}
        <td className={box}>
          <LabeledCell value={String(agency.activeListings)} mono dim={agency.activeListings === 0} />
        </td>
        <td className={box}>
          <LabeledCell value={agency.pageViews.toLocaleString()} mono dim={agency.pageViews === 0} />
        </td>
        <td className={box}>
          <LabeledCell value={agency.listingClicks.toLocaleString()} mono dim={agency.listingClicks === 0} />
        </td>
        <td className={box}>
          <LabeledCell value={String(agency.listingInquiries)} mono dim={agency.listingInquiries === 0} />
        </td>
        <td className={box}>
          <LabeledCell value={String(agency.activeButtons)} mono dim={agency.activeButtons === 0} />
        </td>
        <td className={box}>
          <LabeledCell value={agency.buttonClicks.toLocaleString()} mono dim={agency.buttonClicks === 0} />
        </td>
        <td className={box}>
          <LabeledCell value={formatDate(agency.createdAt)} mono />
        </td>
        <td className={box}>
          <LabeledCell value="—" mono />
        </td>
      </tr>
      {/* Spacer so the box doesn't butt straight into the first agent row. */}
      <tr aria-hidden>
        <td colSpan={colSpan} className="h-3 p-0" />
      </tr>
    </>
  );
}

function ProptreeRow({
  proptree,
  first = false,
  statusFilter,
  showAdminColumns,
}: {
  proptree: ProptreeWithAgent;
  /** First row of the agent group — draws the rule under the heading. */
  first?: boolean;
  statusFilter: StatusFilter;
  showAdminColumns: boolean;
}) {
  const agent = proptree.agent;
  const agentName = agent?.name || '—';
  const agentPhone = agent?.phone || '—';
  const agentImage = agent?.image ?? null;

  const bioChannels = Array.isArray(agent?.linkInBio)
    ? (agent.linkInBio as string[])
    : null;
  const recoveryCode =
    agent?.recoveryCode == null ? '—' : String(agent.recoveryCode);
  const statusLabel = agent?.statusName || '—';
  const adminStatus = renderUnknown(agent?.adminStatus, '—');
  const welcomeStatus = renderUnknown(agent?.welcomeStatus, '— / —');
  const reminderStatus = renderUnknown(agent?.reminderStatus, '— / —');
  const reportStatus = renderUnknown(agent?.reportStatus, '— / —');

  const proptreeId = proptree.shortId || '—';
  const customShortname = proptree.alias ? `@${proptree.alias}` : '—';
  const created = formatDate(proptree.createdAt);

  return (
    <tr
      className={cn(
        'border-b border-[hsl(var(--hairline))] last:border-b-0 transition-colors hover:bg-muted/40',
        first && 'border-t border-t-[hsl(var(--hairline))]',
      )}
    >
      <Cell>
        <div className="flex items-center gap-3">
          <RowActionsMenu proptree={proptree} statusFilter={statusFilter} />
          <ProfileAvatar name={customShortname} image={proptree.profileImage} />
          <div>
            <div className="whitespace-nowrap font-sans text-[14px] font-medium text-foreground">
              {customShortname}
            </div>
            <div className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
              {proptreeId}
            </div>
          </div>
        </div>
      </Cell>
      <Cell>
        <div className="flex items-center gap-3">
          <ProfileAvatar name={agentName} image={agentImage} />
          <div>
            <div className="whitespace-nowrap font-sans text-[14px] font-medium text-foreground">
              {agentName}
            </div>
            <div className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
              {agentPhone}
            </div>
          </div>
        </div>
      </Cell>
      <Cell>
        <BioChannelsCell channels={bioChannels} />
      </Cell>
      <Cell>
        {statusLabel === '—' ? (
          <LabeledCell value="—" />
        ) : (
          <StatusBadge
            label={displayStatus(statusLabel)}
            tone={isActiveStatus(statusLabel) ? 'success' : 'muted'}
          />
        )}
      </Cell>
      {showAdminColumns && (
        <>
          <Cell>
            <LabeledCell value={adminStatus} />
          </Cell>
          <Cell>
            <LabeledCell value={welcomeStatus} mono />
          </Cell>
          <Cell>
            <LabeledCell value={reminderStatus} mono />
          </Cell>
          <Cell>
            <LabeledCell value={reportStatus} mono />
          </Cell>
        </>
      )}
      <Cell>
        <LabeledCell
          value={String(proptree.activeListings)}
          mono
          dim={proptree.activeListings === 0}
        />
      </Cell>
      <Cell>
        <LabeledCell
          value={proptree.pageViews.toLocaleString()}
          mono
          dim={proptree.pageViews === 0}
        />
      </Cell>
      <Cell>
        <LabeledCell
          value={proptree.listingClicks.toLocaleString()}
          mono
          dim={proptree.listingClicks === 0}
        />
      </Cell>
      <Cell>
        <LabeledCell
          value={String(proptree.listingInquiries)}
          mono
          dim={proptree.listingInquiries === 0}
        />
      </Cell>
      <Cell>
        <LabeledCell
          value={String(proptree.activeButtons)}
          mono
          dim={proptree.activeButtons === 0}
        />
      </Cell>
      <Cell>
        <LabeledCell
          value={proptree.buttonClicks.toLocaleString()}
          mono
          dim={proptree.buttonClicks === 0}
        />
      </Cell>
      <Cell>
        <LabeledCell value={created} mono />
      </Cell>
      <Cell>
        <LabeledCell value={recoveryCode} mono />
      </Cell>
    </tr>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-4 py-4 align-middle first:pl-5 last:pr-5">{children}</td>
  );
}

// Compact card shown next to the page title. Two of these sit side-by-side:
// one for agency-proptree settings and one for agent-proptree settings. The
// button sits on the right of the title/subtitle so the card height stays in
// line with the page title block on the left.
// Per-row action menu. Visibility rules mirror legacy enterprise-app
// listProptrees.js (lines ~1151-1300). The manager portal corresponds to
// `level.id == 2` non-admin context: admin-only items (transfer template,
// remove, admin-notification submenu) are intentionally omitted. Welcome
// notification limit is 1 (admin would be 10).
const WELCOME_LIMIT = 1;

function RowActionsMenu({
  proptree,
  statusFilter,
}: {
  proptree: ProptreeWithAgent;
  statusFilter: StatusFilter;
}) {
  const hasShortId = !!proptree.shortId;
  const isAgent = (proptree.type || '').toLowerCase() === 'agent';
  const isCustom = (proptree.type || '').toLowerCase() === 'custom';
  const isAgencyDefault = !!proptree.agencyDefault;
  const isPending = !!proptree.pending;
  const isTransferred = !!proptree.transferred;
  const isEnabled = proptree.status === 'enabled';
  const isDisabled = proptree.status === 'disabled';
  const filterAllowsTemplates =
    statusFilter === 'enabled' || statusFilter === 'all';

  const welcomeWaUsed = proptree.agent?.welcomeWaAmount ?? 0;
  const welcomeEmailUsed = proptree.agent?.welcomeEmailAmount ?? 0;

  // Notifications submenu: welcome WA / welcome email — manager limit is 1.
  const showWelcomeWa =
    isAgent &&
    hasShortId &&
    !isPending &&
    filterAllowsTemplates &&
    welcomeWaUsed < WELCOME_LIMIT;
  const showWelcomeEmail =
    isAgent &&
    hasShortId &&
    !isPending &&
    filterAllowsTemplates &&
    welcomeEmailUsed < WELCOME_LIMIT;
  const showNotifications = showWelcomeWa || showWelcomeEmail;

  // Action visibility (mirrors legacy filter chain).
  const showClone = isCustom && !isAgencyDefault;
  const showEdit = filterAllowsTemplates && !isAgent && hasShortId;
  const showEnable = isDisabled && hasShortId && !isTransferred;
  const showCreate = !hasShortId;
  const showOpen = hasShortId;
  const showLogin = hasShortId && !isPending;
  const showDisable = isEnabled && hasShortId && !isPending;

  const groups: React.ReactNode[][] = [];

  const publicSlug = proptree.alias || proptree.shortId;
  const agentUserId = proptree.agent?._id;
  // Test mode: only "Open Proptree" stays live (it opens the demo page).
  // Everything else here mutates real records or mints a real session,
  // and the seeded proptrees have no backing docs to act on.
  const testMode = isTestMode();

  const handleOpen = () => {
    if (!publicSlug) return;
    window.open(
      withDemoFlag(`${PUBLIC_URL}/${encodeURIComponent(publicSlug)}`),
      '_blank',
      'noopener,noreferrer',
    );
  };

  // "Login as Proptree" → mint a one-shot impersonation JWT for the
  // agent and open the agent portal in a new tab with `?_imp=<token>`.
  // Manager's own session cookie is left untouched (the API endpoint
  // doesn't change it), so this tab keeps working.
  const handleLogin = async () => {
    if (!agentUserId) return;
    try {
      const { impToken } = await client.auth.loginAsAgent({
        userId: agentUserId,
      });
      window.open(
        `${APP_URL}/?_imp=${encodeURIComponent(impToken)}`,
        '_blank',
        'noopener,noreferrer',
      );
    } catch (err) {
      // Surface failure inline — most common cause is the target agent
      // being inactive or no longer in the manager's enterprise.
      window.alert(
        `Could not open agent portal: ${(err as Error).message}`,
      );
    }
  };

  // Group 1: open / login (the most common reads)
  const navItems: React.ReactNode[] = [];
  if (showOpen) {
    navItems.push(
      <DropdownMenuItem
        key="open"
        onSelect={handleOpen}
        disabled={!publicSlug}
      >
        <span className="flex-1">Open Proptree</span>
        <ExternalLink />
      </DropdownMenuItem>,
    );
  }
  if (showLogin) {
    navItems.push(
      <DropdownMenuItem
        key="login"
        onSelect={() => void handleLogin()}
        disabled={!agentUserId || testMode}
      >
        <span className="flex-1">Login as Proptree</span>
        <LogIn />
      </DropdownMenuItem>,
    );
  }
  if (showCreate) {
    navItems.push(
      <DropdownMenuItem key="create" onSelect={() => {}} disabled={testMode}>
        <span className="flex-1">Create Proptree</span>
        <UserPlus />
      </DropdownMenuItem>,
    );
  }
  if (navItems.length) groups.push(navItems);

  // Group 2: edit / clone
  const editItems: React.ReactNode[] = [];
  if (showEdit) {
    editItems.push(
      <DropdownMenuItem key="edit" onSelect={() => {}} disabled={testMode}>
        <span className="flex-1">Edit</span>
        <Edit3 />
      </DropdownMenuItem>,
    );
  }
  if (showClone) {
    editItems.push(
      <DropdownMenuItem key="clone" onSelect={() => {}} disabled={testMode}>
        <span className="flex-1">Clone</span>
        <UserPlus />
      </DropdownMenuItem>,
    );
  }
  if (editItems.length) groups.push(editItems);

  // Group 3: notifications submenu
  if (showNotifications) {
    groups.push([
      <DropdownMenuSub key="notifications">
        <DropdownMenuSubTrigger disabled={testMode}>
          <Bell />
          <span>Notifications</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {showWelcomeEmail && (
            <DropdownMenuItem onSelect={() => {}}>
              <span className="flex-1">Send welcome email</span>
              <Mail />
            </DropdownMenuItem>
          )}
          {showWelcomeWa && (
            <DropdownMenuItem onSelect={() => {}}>
              <span className="flex-1">Send welcome WhatsApp</span>
              <Smartphone />
            </DropdownMenuItem>
          )}
        </DropdownMenuSubContent>
      </DropdownMenuSub>,
    ]);
  }

  // Group 4: state changes (enable / disable)
  const stateItems: React.ReactNode[] = [];
  if (showEnable) {
    stateItems.push(
      <DropdownMenuItem
        key="enable"
        onSelect={() => {}}
        disabled={testMode}
        className="text-emerald-600 focus:text-emerald-700 [&_svg]:text-emerald-600"
      >
        <span className="flex-1">Enable</span>
        <CheckCircle2 />
      </DropdownMenuItem>,
    );
  }
  if (showDisable) {
    stateItems.push(
      <DropdownMenuItem
        key="disable"
        onSelect={() => {}}
        disabled={testMode}
        className="text-destructive focus:text-destructive [&_svg]:text-destructive"
      >
        <span className="flex-1">Disable</span>
        <MinusCircle />
      </DropdownMenuItem>,
    );
  }
  if (stateItems.length) groups.push(stateItems);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:bg-muted focus-visible:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
        >
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[14rem]">
        {groups.length === 0 ? (
          <DropdownMenuItem disabled>No actions available</DropdownMenuItem>
        ) : (
          groups.map((group, gi) => (
            <React.Fragment key={gi}>
              {gi > 0 && <DropdownMenuSeparator />}
              {group}
            </React.Fragment>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LabeledCell({
  value,
  mono,
  dim,
}: {
  value: string;
  mono?: boolean;
  dim?: boolean;
}) {
  return (
    <div
      className={cn(
        mono
          ? 'whitespace-nowrap font-mono text-[12px] font-medium tabular-nums text-foreground/80'
          : 'whitespace-nowrap font-sans text-[13px] font-medium text-foreground',
        dim && 'text-muted-foreground',
      )}
    >
      {value}
    </div>
  );
}

function PillCell({
  value,
  tone,
}: {
  value: string;
  tone: 'muted' | 'accent';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 font-sans text-[12px] font-medium',
        tone === 'accent'
          ? 'bg-primary/10 text-primary'
          : 'bg-muted text-foreground',
      )}
    >
      {value}
    </span>
  );
}

// Channel badges are shared with the affiliate list — see
// components/BioChannelsCell.tsx.

function ProfileAvatar({
  name,
  image,
}: {
  name: string;
  image?: string | null;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  if (name && name !== '—') {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
        {initials(name)}
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
      <UserIcon className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function renderUnknown(value: unknown, fallback: string): string {
  if (value == null) return fallback;
  if (typeof value === 'string') return value || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}
